import { useState, useEffect, useRef, useMemo } from 'react';
import WatchZoneManager from './WatchZoneManager';
import HelpTip from './HelpTip';
import { GMAP_KEY, MAP_CENTRES } from '../lib/constants';
import { sStyle, sAddr, cShort, findClosestPreset, haversine, hasValidCoords } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';
import { displayDAIdentifier } from '../lib/daDisplay';
import { getPipelineCost } from '../lib/pipelineCost';
import { getDwellings } from '../lib/daMeasurements';
import { watchZoneCamera } from '../lib/watchZoneCamera';
import { subscribeToGoogleMaps } from '../lib/googleMapsLoader-v1';

const STREET_MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'on' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#ebe6dc' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ visibility: 'on' }, { color: '#cfdcbd' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#bdd8df' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#d8d2c7' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6d685e' }] },
  { featureType: 'road', elementType: 'labels.text.stroke', stylers: [{ color: '#f5f1ea' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#45413a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5f5a50' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f1ea' }] },
];

// ── Compact inline popup (fixes iPhone overflow bug) ──────────────────────────
function MapPopupCard({ app, onViewDetails, onClose }) {
  function formatCost(cost) {
    if (!cost || cost === 0) return null;
    if (cost >= 1000000) return `$${(cost / 1000000).toFixed(1)}M`;
    if (cost >= 1000) return `$${(cost / 1000).toFixed(0)}K`;
    return `$${cost.toLocaleString()}`;
  }
  const cost = formatCost(getPipelineCost(app));
  const dwellings = getDwellings(app);
  const addr = sAddr(app.address || '');
  const daNum = displayDAIdentifier(app);

  return (
    <div style={{
      background: '#fbfaf7', border: '1px solid rgba(10,15,26,0.08)',
      borderRadius: 8, padding: '12px 14px',
      width: 244, fontFamily: "'DM Sans', sans-serif",
      boxShadow: '0 12px 32px rgba(10,15,26,0.22)',
      position: 'relative',
    }}>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', color: '#8a8578', fontSize: 18, cursor: 'pointer', padding: '2px 4px', lineHeight: 1, zIndex: 1 }}
        aria-label="Close"
      >×</button>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5, paddingRight: 18 }}>
        <span style={{ color: '#52606d', fontSize: 11, fontWeight: 700, letterSpacing: 0.2 }}>{daNum}</span>
      </div>
      <div style={{ color: '#16202c', fontSize: 14, fontWeight: 800, marginBottom: 4, paddingRight: 16, lineHeight: 1.25 }}>
        {addr.split(',')[0]}
      </div>
      <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 10 }}>
        {[app.suburb, app.category].filter(Boolean).join(' · ')}
      </div>
      <div style={{ height: 1, background: '#e4dfd7', marginBottom: 9 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
        <div>
          <div style={{ color: '#8a8578', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>Est. Value</div>
          <div style={{ color: '#16202c', fontSize: 13, fontWeight: 800 }}>{cost || '-'}</div>
        </div>
        {dwellings > 0 && (
          <div>
            <div style={{ color: '#8a8578', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>Dwellings</div>
            <div style={{ color: '#16202c', fontSize: 13, fontWeight: 800 }}>{dwellings}</div>
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        <button
          onClick={onViewDetails}
          style={{ background: 'none', border: 'none', color: '#b98316', fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
        >View details →</button>
      </div>
    </div>
  );
}

// ── GMap with cluster markers, watch-zone rings, regional overlay ─────────────
function GMap({ insideDAs, outsideDAs, totalCount, zonesOnlyMode, zones, onSelectApp, onMapClick, onSelectZone, selectedZone, onLongPressAdd, isDark, theme, userLocation, panToRef, focusDA, pinnedDAId, onFocusHandled, onZoomChange }) {
  const [mapFailure, setMapFailure] = useState(null);
  const [mapAttempt, setMapAttempt] = useState(0);
  const tileTimerRef = useRef(null);
  const ref        = useRef(null);
  const mapRef     = useRef(null);
  const markersRef = useRef([]);
  const circlesRef = useRef([]);
  const clusterRef = useRef(null);
  const userMarkerRef = useRef(null);
  const suppressNextMapClickRef = useRef(false);
  const onLPRef    = useRef(onLongPressAdd);
  useEffect(() => { onLPRef.current = onLongPressAdd; }, [onLongPressAdd]);
  const onMapClickRef = useRef(onMapClick);
  useEffect(() => { onMapClickRef.current = onMapClick; }, [onMapClick]);
  const onSelectZoneRef = useRef(onSelectZone);
  useEffect(() => { onSelectZoneRef.current = onSelectZone; }, [onSelectZone]);
  const isDarkRef  = useRef(isDark);
  useEffect(() => { isDarkRef.current = isDark; }, [isDark]);
  const themeRef   = useRef(theme);
  useEffect(() => { themeRef.current = theme; }, [theme]);
  const insideCurrent   = useRef(insideDAs);
  useEffect(() => { insideCurrent.current = insideDAs; }, [insideDAs]);
  const outsideCurrent  = useRef(outsideDAs);
  useEffect(() => { outsideCurrent.current = outsideDAs; }, [outsideDAs]);
  const zonesOnlyCurrent = useRef(zonesOnlyMode);
  useEffect(() => { zonesOnlyCurrent.current = zonesOnlyMode; }, [zonesOnlyMode]);
  const zonesCurrent = useRef(zones);
  useEffect(() => { zonesCurrent.current = zones; }, [zones]);
  const selectedZoneRef = useRef(selectedZone);
  useEffect(() => { selectedZoneRef.current = selectedZone; }, [selectedZone]);
  const pinnedDAIdRef = useRef(pinnedDAId);
  useEffect(() => { pinnedDAIdRef.current = pinnedDAId; }, [pinnedDAId]);
  const focusDARef = useRef(null);
  useEffect(() => { focusDARef.current = focusDA; }, [focusDA]);

  function fitWatchZoneBounds(map, zone) {
    const camera = watchZoneCamera(zone, ref.current?.clientWidth, ref.current?.clientHeight);
    if (map && camera) map.moveCamera(camera);
  }

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    mapRef.current.setMapTypeId(google.maps.MapTypeId.ROADMAP);
    mapRef.current.setOptions({ styles: STREET_MAP_STYLES });
  }, [isDark]);

  useEffect(() => {
    if (!mapRef.current || !userLocation || !window.google?.maps) return;
    if (userMarkerRef.current) { userMarkerRef.current.setMap(null); userMarkerRef.current = null; }
    userMarkerRef.current = new google.maps.Marker({
      map: mapRef.current,
      position: { lat: userLocation.lat, lng: userLocation.lng },
      icon: { path: google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: '#4a90e2', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2.5 },
      title: 'Your location',
      zIndex: 999,
    });
  }, [userLocation]);

  // Load Google Maps, then MarkerClusterer (must load AFTER Maps so google.maps exists)
  useEffect(() => {
    if (!GMAP_KEY) return;
    setMapFailure(null);
    let cancelled = false;
    let clusterScript;
    let clusterTimer;
    let initialised = false;
    const initialiseIfMounted = () => {
      if (cancelled || initialised) return;
      initialised = true;
      clearTimeout(clusterTimer);
      try { init(); } catch {
        // A partially downloaded Maps library can fail during construction.
        clearTimeout(tileTimerRef.current);
        setMapFailure('initialisation');
      }
    };
    const loadClustererThenInit = () => {
      if (cancelled) return;
      if (window.markerClusterer) { initialiseIfMounted(); return; }
      clusterScript = document.createElement('script');
      clusterScript.src = 'https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';
      clusterScript.onload = initialiseIfMounted;
      clusterScript.onerror = initialiseIfMounted; // fallback: show individual pins
      clusterTimer = setTimeout(initialiseIfMounted, 8000);
      document.head.appendChild(clusterScript);
    };
    const unsubscribe = subscribeToGoogleMaps({
      apiKey: GMAP_KEY,
      onReady: loadClustererThenInit,
      onError: () => { if (!cancelled) setMapFailure('download'); },
    });

    return () => {
      cancelled = true;
      unsubscribe();
      clearTimeout(clusterTimer);
      clearTimeout(tileTimerRef.current);
      if (clusterScript) {
        clusterScript.onload = null;
        clusterScript.onerror = null;
        clusterScript.remove();
      }
      clusterRef.current?.clearMarkers();
      clusterRef.current?.setMap(null);
      clusterRef.current = null;
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      circlesRef.current.forEach(circle => circle.setMap(null));
      circlesRef.current = [];
      userMarkerRef.current?.setMap(null);
      userMarkerRef.current = null;
      if (mapRef.current) window.google?.maps?.event.clearInstanceListeners(mapRef.current);
      mapRef.current = null;
      if (panToRef) panToRef.current = null;
      onZoomChange?.(null);
    };
  }, [mapAttempt]);

  function init() {
    if (!ref.current || mapRef.current) return;
    let defaultView = 'all';
    try { const c = JSON.parse(localStorage.getItem('sitescope-user-prefs')); if (c?.default_map_view) defaultView = c.default_map_view; } catch { /* ignore */ }
    const centre = MAP_CENTRES[defaultView] || MAP_CENTRES.all;

    const map = new google.maps.Map(ref.current, {
      center: selectedZoneRef.current && !focusDARef.current
        ? { lat: selectedZoneRef.current.lat, lng: selectedZoneRef.current.lng }
        : { lat: centre.lat, lng: centre.lng },
      zoom: centre.zoom,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      styles: STREET_MAP_STYLES,
      disableDefaultUI: true,
      // We handle zoom via custom buttons — no native control
      gestureHandling: 'greedy',
      disableDoubleClickZoom: true,
      clickableIcons: false,
      keyboardShortcuts: false,
      tilt: 0,
      minZoom: 9,
      maxZoom: 18,
      restriction: {
        latLngBounds: { north: -27.8, south: -30.0, east: 154.0, west: 151.6 },
        strictBounds: false,
      },
    });
    mapRef.current = map;
    tileTimerRef.current = setTimeout(() => setMapFailure('tiles'), 20000);
    map.addListener('tilesloaded', () => {
      clearTimeout(tileTimerRef.current);
      setMapFailure(failure => failure === 'tiles' ? null : failure);
    });

    if (panToRef) panToRef.current = (loc) => { map.panTo({ lat: loc.lat, lng: loc.lng }); map.setZoom(14); };

    // Expose zoom method for external controls
    if (onZoomChange) onZoomChange(map);

    if (!updateMarkers()) return;

    // A selected area can arrive before the asynchronous Maps/clusterer scripts.
    // Apply it at readiness too; the selectedZone effect may already have run.
    if (selectedZoneRef.current && !focusDARef.current) {
      fitWatchZoneBounds(map, selectedZoneRef.current);
    }

    // Focus DA that was set before map finished initialising
    if (hasValidCoords(focusDARef.current)) {
      const da = focusDARef.current;
      map.panTo({ lat: da.latitude, lng: da.longitude });
      map.setZoom(17);
      const marker = markersRef.current.find(m => m.daId === da.id);
      if (marker) google.maps.event.trigger(marker, 'click');
      onFocusHandled?.();
    }

    // Long-press zone adder (double-click)
    const lpIW = new google.maps.InfoWindow();
    const handleLP = (latLng) => {
      const { preset, distKm } = findClosestPreset(latLng);
      if (!preset) return;
      const far = distKm > 50;
      const t   = themeRef.current;
      const ba  = isDarkRef.current ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)';
      lpIW.setContent(`<div style="background:${t.bgCard};padding:16px;border-radius:8px;font-family:Calibri,sans-serif;min-width:220px"><div style="color:${t.text};font-family:Georgia,serif;font-size:14px;font-weight:700;margin-bottom:${far?'4px':'10px'}">Add Watch Zone here?</div>${far?`<div style="color:${t.textMuted};font-size:11px;margin-bottom:10px">Nearest: ${preset.name} (${Math.round(distKm)}km away)</div>`:''}<div style="color:${t.accent};font-size:15px;font-weight:bold;margin-bottom:2px">📍 ${preset.name}</div><div style="color:${t.textMuted};font-size:12px;margin-bottom:10px">${preset.council}</div><div style="font-size:11px;color:${t.textMuted};margin-bottom:6px;letter-spacing:0.5px">Radius</div><div style="display:flex;gap:6px;margin-bottom:12px"><button id="lp-r-2000" style="flex:1;padding:7px 0;border-radius:6px;border:1px solid ${ba};background:transparent;color:${t.text};font-family:Calibri;font-size:13px;cursor:pointer">2km</button><button id="lp-r-5000" style="flex:1;padding:7px 0;border-radius:6px;border:none;background:${t.accent};color:${t.textDark};font-family:Calibri;font-size:13px;font-weight:bold;cursor:pointer">5km</button><button id="lp-r-10000" style="flex:1;padding:7px 0;border-radius:6px;border:1px solid ${ba};background:transparent;color:${t.text};font-family:Calibri;font-size:13px;cursor:pointer">10km</button></div><div id="lp-radius-val" data-radius="5000" style="display:none"></div><div style="display:flex;gap:8px"><button id="lp-add" style="background:${t.accent};color:${t.textDark};border:none;padding:8px 16px;border-radius:6px;cursor:pointer;font-weight:bold;font-family:Calibri">Add Zone</button><button id="lp-cancel" style="background:transparent;color:${t.text};border:1px solid ${ba};padding:8px 16px;border-radius:6px;cursor:pointer;font-family:Calibri">Cancel</button></div></div>`);
      lpIW.setPosition(latLng);
      lpIW.open(map);
      google.maps.event.addListenerOnce(lpIW, 'domready', () => {
        const ct = themeRef.current;
        const iwContainer = document.getElementById('lp-radius-val')?.closest('.gm-style-iw-c');
        if (iwContainer) { iwContainer.style.background = ct.bgCard; iwContainer.style.border = `1px solid ${ct.border}`; iwContainer.style.boxShadow = 'none'; iwContainer.style.padding = '0'; const cb = iwContainer.querySelector('button.gm-ui-hover-effect'); if (cb) cb.style.display = 'none'; }
        const iwInner = document.querySelector('.gm-style-iw-d');
        if (iwInner) { iwInner.style.overflow = 'hidden'; iwInner.style.background = ct.bgCard; }
        const radBtns = [{ id: 'lp-r-2000', val: 2000 }, { id: 'lp-r-5000', val: 5000 }, { id: 'lp-r-10000', val: 10000 }];
        radBtns.forEach(({ id, val }) => {
          document.getElementById(id)?.addEventListener('click', () => {
            document.getElementById('lp-radius-val').dataset.radius = val;
            const bt = themeRef.current; const ba2 = isDarkRef.current ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)';
            radBtns.forEach(b => {
              const btn = document.getElementById(b.id); if (!btn) return;
              if (b.val === val) { btn.style.background = bt.accent; btn.style.color = bt.textDark; btn.style.border = 'none'; btn.style.fontWeight = 'bold'; }
              else { btn.style.background = 'transparent'; btn.style.color = bt.text; btn.style.border = `1px solid ${ba2}`; btn.style.fontWeight = 'normal'; }
            });
          });
        });
        document.getElementById('lp-add')?.addEventListener('click', () => { const radius = parseInt(document.getElementById('lp-radius-val')?.dataset.radius || '5000'); onLPRef.current?.(preset, radius); lpIW.close(); });
        document.getElementById('lp-cancel')?.addEventListener('click', () => lpIW.close());
      });
    };

    map.addListener('click', () => {
      if (suppressNextMapClickRef.current) return;
      onMapClickRef.current?.();
    });
    map.addListener('dblclick', (e) => { handleLP(e.latLng); });
  }

  function shiftHexColor(hex, amount) {
    const clean = /^#[0-9a-f]{6}$/i.test(hex) ? hex.slice(1) : 'c0a060';
    const next = clean.match(/.{2}/g).map(part => {
      const value = Math.max(0, Math.min(255, parseInt(part, 16) + amount));
      return value.toString(16).padStart(2, '0');
    });
    return `#${next.join('')}`;
  }

  function findDAZone(app, zs) {
    if (!hasValidCoords(app) || !zs.length) return null;
    return zs
      .map(z => ({ zone: z, distance: haversine(app.latitude, app.longitude, z.lat, z.lng) }))
      .filter(({ zone, distance }) => distance <= zone.radius)
      .sort((a, b) => a.distance - b.distance)[0]?.zone || null;
  }

  function dominantZoneColor(markers, fallback = '#c0a060') {
    const counts = new Map();
    (markers || []).forEach(marker => {
      if (!marker.zoneColor) return;
      counts.set(marker.zoneColor, (counts.get(marker.zoneColor) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || fallback;
  }

  function makeClusterSvg(count, size, color = '#c0a060') {
    const outer = shiftHexColor(color, -22);
    const inner = shiftHexColor(color, 18);
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <defs>
        <radialGradient id="cg" cx="40%" cy="35%">
          <stop offset="0%" stop-color="${inner}"/>
          <stop offset="100%" stop-color="${outer}"/>
        </radialGradient>
        <filter id="cs" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="rgba(10,15,26,0.34)"/>
        </filter>
      </defs>
      <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 2}" fill="url(#cg)" filter="url(#cs)" stroke="rgba(255,255,255,0.65)" stroke-width="2"/>
      <text x="${size/2}" y="${size/2 + 1}" text-anchor="middle" dominant-baseline="central" fill="#ffffff" font-family="DM Sans, sans-serif" font-weight="800" font-size="${size > 44 ? 17 : 14}">${count}</text>
    </svg>`;
  }

  function makePinSvg(color, muted = false) {
    const opacity = muted ? 0.62 : 1;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="42" viewBox="0 0 34 42">
      <filter id="ps" x="-30%" y="-20%" width="160%" height="160%">
        <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="rgba(10,15,26,0.32)"/>
      </filter>
      <path filter="url(#ps)" opacity="${opacity}" d="M17 40s13-12.2 13-24A13 13 0 1 0 4 16c0 11.8 13 24 13 24z" fill="${color}" stroke="#ffffff" stroke-width="3"/>
      <circle cx="17" cy="16" r="4.8" fill="#ffffff"/>
    </svg>`;
  }

  function handleMarkerSelect(app, event) {
    suppressNextMapClickRef.current = true;
    event?.domEvent?.stopPropagation?.();
    onSelectApp?.(app);
    window.setTimeout(() => { suppressNextMapClickRef.current = false; }, 0);
  }

  function updateMarkers() {
    try {
      updateMapMarkers();
      return true;
    } catch {
      clearTimeout(tileTimerRef.current);
      setMapFailure('initialisation');
      return false;
    }
  }

  function updateMapMarkers() {
    const map     = mapRef.current; if (!map || !window.google?.maps) return;
    const inside  = insideCurrent.current;
    const outside = outsideCurrent.current;
    const zonesOnly = zonesOnlyCurrent.current;
    const zs      = zonesCurrent.current;
    const selected = selectedZoneRef.current;

    // Clear existing markers and clusters
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    circlesRef.current.forEach(c => c.setMap(null));
    circlesRef.current = [];
    if (clusterRef.current) {
      clusterRef.current.clearMarkers();
      clusterRef.current.setMap(null);
      clusterRef.current = null;
    }

    // Watch zone circles + premium labels
    zs.forEach(z => {
      const isSelected = selected?.id === z.id;
      const zoneColor = z.color || '#c0a060';
      const circle = new google.maps.Circle({
        map,
        center: { lat: z.lat, lng: z.lng },
        radius: z.radius,
        strokeColor: zoneColor,
        strokeWeight: isSelected ? 2.5 : 2,
        strokeOpacity: isSelected ? 0.9 : 0.72,
        fillColor: zoneColor,
        fillOpacity: isSelected ? 0.18 : 0.09,
        clickable: true,
      });
      circle.addListener('click', () => {
        onSelectZoneRef.current?.(z);
        fitWatchZoneBounds(map, z);
      });
      circlesRef.current.push(circle);

      const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="98" height="26" viewBox="0 0 98 26">
        <rect width="98" height="26" rx="6" fill="#0a0f1a" stroke="${zoneColor}" stroke-width="${isSelected ? 1.4 : 1}" opacity="0.94"/>
        <text x="49" y="17" text-anchor="middle" fill="${zoneColor}" font-family="DM Sans, sans-serif" font-weight="800" font-size="10">WATCH · ${Math.round(z.radius/1000)}KM</text>
      </svg>`;
      const labelOffset = z.radius / 111000;
      const zoneLabel = new google.maps.Marker({
        map,
        position: { lat: z.lat + labelOffset * 1.15, lng: z.lng },
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
          scaledSize: new google.maps.Size(98, 26),
          anchor: new google.maps.Point(49, 13),
        },
        clickable: true,
        zIndex: isSelected ? 30 : 5,
      });
      zoneLabel.addListener('click', () => {
        onSelectZoneRef.current?.(z);
        fitWatchZoneBounds(map, z);
      });
      markersRef.current.push(zoneLabel);
    });

    // De-emphasised pins (outside zones)
    if (!zonesOnly) {
      outside.forEach(a => {
        if (!hasValidCoords(a)) return;
        const marker = new google.maps.Marker({
          map,
          position: { lat: a.latitude, lng: a.longitude },
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(makePinSvg('#8090a8', true))}`,
            scaledSize: new google.maps.Size(26, 32),
            anchor: new google.maps.Point(13, 31),
          },
          title: sAddr(a.address),
          zIndex: 1,
        });
        marker.daId = a.id;
        marker.addListener('click', (event) => { handleMarkerSelect(a, event); });
        markersRef.current.push(marker);
      });
    }

    // Inside-zone markers — use MarkerClusterer if available
    const insideMarkers = inside.map(a => {
      if (!hasValidCoords(a)) return null;
      const zone = findDAZone(a, zs);
      const ss = sStyle(a.status);
      const color = zone?.color || ss.c || '#c0a060';
      const marker = new google.maps.Marker({
        position: { lat: a.latitude, lng: a.longitude },
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(makePinSvg(color))}`,
          scaledSize: new google.maps.Size(30, 37),
          anchor: new google.maps.Point(15, 36),
        },
        title: sAddr(a.address),
        zIndex: 10,
      });
      marker.daId = a.id;
      marker.zoneColor = color;
      marker.addListener('click', (event) => { handleMarkerSelect(a, event); });
      return marker;
    }).filter(Boolean);

    const focusedMarker = insideMarkers.find(marker => marker.daId === pinnedDAIdRef.current);
    if (focusedMarker) focusedMarker.setMap(map);
    const clusterMarkers = insideMarkers.filter(marker => marker !== focusedMarker);

    // Use MarkerClusterer if the CDN lib loaded
    const MC = window.markerClusterer?.MarkerClusterer;
    if (MC && insideMarkers.length > 0) {
      const clusterRenderer = {
        render: ({ count, position, markers }) => {
          const size = Math.min(60, Math.max(36, 20 + count * 0.8));
          const color = dominantZoneColor(markers, '#c0a060');
          return new google.maps.Marker({
            position,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(makeClusterSvg(count, size, color))}`,
              scaledSize: new google.maps.Size(size, size),
              anchor: new google.maps.Point(size / 2, size / 2),
            },
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });
        },
      };
      clusterRef.current = new MC({ map, markers: clusterMarkers, renderer: clusterRenderer });
    } else {
      // Fallback: add markers directly to map
      insideMarkers.forEach(m => m.setMap(map));
    }
    markersRef.current.push(...insideMarkers);
  }

  useEffect(() => { if (mapRef.current) updateMarkers(); }, [insideDAs, outsideDAs, zonesOnlyMode, zones]);
  useEffect(() => { if (mapRef.current) updateMarkers(); }, [selectedZone]);
  useEffect(() => {
    if (selectedZone && mapRef.current) fitWatchZoneBounds(mapRef.current, selectedZone);
  }, [selectedZone]);
  useEffect(() => {
    if (!hasValidCoords(focusDA) || !mapRef.current || !window.google?.maps) return;
    mapRef.current.panTo({ lat: focusDA.latitude, lng: focusDA.longitude });
    mapRef.current.setZoom(17);
    const marker = markersRef.current.find(m => m.daId === focusDA.id);
    if (marker) google.maps.event.trigger(marker, 'click');
    onFocusHandled?.();
  }, [focusDA]);

  return <>
    <div ref={ref} style={{ width: '100%', height: '100%', userSelect: 'none' }} />
    {mapFailure && <div role="alert" style={{ position: 'absolute', inset: '55px 16px auto', zIndex: 20, maxWidth: 360, margin: '0 auto', background: theme.bgCard || '#fbfaf7', color: theme.text || '#16202c', padding: 20, borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
      <strong>The map hasn’t finished loading</strong>
      <p style={{ fontSize: 14, lineHeight: 1.5 }}>Your connection may have been interrupted. You can retry or keep browsing projects in the Dashboard.</p>
      <button type="button" onClick={() => mapFailure === 'download' ? setMapAttempt(attempt => attempt + 1) : window.location.reload()} style={{ minHeight: 44, padding: '8px 16px', fontSize: 16, cursor: 'pointer' }}>
        {mapFailure === 'download' ? 'Try map again' : 'Reload map'}
      </button>
    </div>}
  </>;
}

// ── Main MapView ──────────────────────────────────────────────────────────────

export function partitionMapDAs(filt, zones, pinnedDA) {
    const plotted = filt.filter(hasValidCoords).filter(a => a.id !== pinnedDA?.id);
    if (hasValidCoords(pinnedDA)) plotted.push(pinnedDA);
    if (zones.length === 0) return { insideDAs: plotted, outsideDAs: [] };
    const inside = [], outside = [];
    plotted.forEach(a => {
      const inZone = a.id === pinnedDA?.id || zones.some(z => haversine(a.latitude, a.longitude, z.lat, z.lng) <= z.radius);
      (inZone ? inside : outside).push(a);
    });
    return { insideDAs: inside, outsideDAs: outside };
}

export default function MapView({ filt, zones, isDark, theme, loading, togTrack, isTr, selZone, setSelZone, addZone, addZoneFromMap, deleteZone, updateZoneRadius, zApps, mapFocusDA, setMapFocusDA, onShowOnMap, onViewDADetails }) {
  const isMobile = useIsMobile();
  const [mapApp, setMapApp]               = useState(mapFocusDA || null);
  const [pinnedDA, setPinnedDA] = useState(mapFocusDA || null);
  useEffect(() => {
    if (mapFocusDA) { setPinnedDA(mapFocusDA); setMapApp(mapFocusDA); }
  }, [mapFocusDA]);
  const [userLocation, setUserLocation]   = useState(null);
  const [zonesOnlyMode, setZonesOnlyMode] = useState(() => {
    try {
      return localStorage.getItem('sitescope-map-zones-only') === 'true';
    } catch {
      return false;
    }
  });
  const panToRef = useRef(null);
  const mapInstanceRef = useRef(null); // for custom zoom buttons

  useEffect(() => {
    try {
      localStorage.setItem('sitescope-map-zones-only', zonesOnlyMode ? 'true' : 'false');
    } catch {
      /* ignore storage failures */
    }
  }, [zonesOnlyMode]);

  // Split plotted DAs into inside / outside zones. DAs without coordinates stay off-map
  // until the data pipeline can geocode them accurately.
  const { insideDAs, outsideDAs } = useMemo(() => {
    return partitionMapDAs(filt, zones, pinnedDA);
  }, [filt, zones, pinnedDA]);

  const [locToast, setLocToast] = useState(null);
  const locToastTimer = useRef(null);
  const showToast = (msg) => {
    clearTimeout(locToastTimer.current);
    setLocToast(msg);
    locToastTimer.current = setTimeout(() => setLocToast(null), 3000);
  };

  const handleLocateMe = () => {
    if (!navigator.geolocation) { showToast('Location unavailable'); return; }
    if (userLocation) { panToRef.current?.(userLocation); return; }
    navigator.geolocation.getCurrentPosition(
      pos => { const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude }; setUserLocation(loc); panToRef.current?.(loc); },
      () => { showToast('Location unavailable'); }
    );
  };

  const handleZoom = (dir) => {
    if (!mapInstanceRef.current) return;
    const current = mapInstanceRef.current.getZoom();
    mapInstanceRef.current.setZoom(current + dir);
  };

  const totalVisible = insideDAs.length + (zonesOnlyMode ? 0 : outsideDAs.length);
  const selectedZoneApps = selZone ? zApps(selZone) : [];

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0' : '0 16px', boxSizing: 'border-box', background: '#0a0f1a' }}>
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 52px)', background: '#0a0f1a' }}>

      {/* Map container */}
      <div style={{
        flex: 1, position: 'relative', minHeight: '300px',
        margin: '0 0 0 0',
      }}>
        {GMAP_KEY ? (
          <GMap
            insideDAs={insideDAs}
            outsideDAs={outsideDAs}
            totalCount={totalVisible}
            zonesOnlyMode={zonesOnlyMode}
            zones={zones}
            onSelectApp={a => setMapApp(a)}
            onMapClick={() => setMapApp(null)}
            onSelectZone={setSelZone}
            selectedZone={selZone}
            onLongPressAdd={addZoneFromMap}
            isDark={isDark}
            theme={theme}
            userLocation={userLocation}
            panToRef={panToRef}
            focusDA={mapFocusDA}
            pinnedDAId={pinnedDA?.id}
            onFocusHandled={() => setMapFocusDA?.(null)}
            onZoomChange={map => { mapInstanceRef.current = map; }}
          />
        ) : (
          <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'#0a1b36',color:'#a8b8cc',padding:24,textAlign:'center',boxSizing:'border-box',fontFamily:"'DM Sans', sans-serif" }}>
            <div>
              <div style={{ color:'#e8e0d8',fontSize:16,fontWeight:800,marginBottom:6 }}>Map unavailable</div>
              <div style={{ fontSize:13,lineHeight:1.5 }}>Google Maps needs an API key before the map can load.</div>
            </div>
          </div>
        )}

        {/* Overlay the breadcrumb so area selection never resizes Google Maps. */}
        {selZone && (
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, background: '#0a0f1a', display: 'flex', alignItems: 'center', height: 40, boxSizing: 'border-box', gap: 8, padding: '8px 16px', overflowX: 'auto', overflowY: 'hidden', lineHeight: '16px', flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <button
            onClick={() => setSelZone(null)}
            style={{ background: 'none', border: 'none', color: '#8a95a8', fontSize: 13, cursor: 'pointer', padding: '4px 0', fontFamily: 'inherit', flexShrink: 0 }}
          >
            ‹ Regional
          </button>
          <span style={{ color: '#435064', fontSize: 12, flexShrink: 0 }}>›</span>
          <span style={{ color: '#e8e0d8', fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}>{selZone.name}</span>
          <span style={{ marginLeft: 'auto', color: '#c0a060', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {selectedZoneApps.length} visible
          </span>
      </div>
        )}

        {/* Loading overlay */}
        {loading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(8,21,37,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ color: '#c0a060', fontSize: 14, fontWeight: 500, fontFamily: 'DM Sans, sans-serif' }}>Loading DAs…</div>
          </div>
        )}

        {/* Regional / focused view overlay */}
        {GMAP_KEY && <div style={{
          position: 'absolute', top: selZone ? 52 : 12, left: 14, zIndex: 10,
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(10,15,26,0.88)',
          padding: '5px 12px', borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(8px)',
          pointerEvents: 'none',
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#5aba6a', flexShrink: 0 }} />
          <span style={{ color: '#f0f2f6', fontSize: 13, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>
            {selZone ? `Local view · ${selZone.name}` : `Regional view · ${insideDAs.length} DA${insideDAs.length !== 1 ? 's' : ''}`}
          </span>
        </div>}

        {/* Custom zoom controls — top right */}
        {GMAP_KEY && <div style={{
          position: 'absolute', top: selZone ? 52 : 12, right: 14, zIndex: 10,
          display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          <button
            onClick={() => handleZoom(1)}
            aria-label="Zoom in"
            style={{ width: 32, height: 32, background: '#0a1b36', border: '1px solid #30404e', borderRadius: '6px 6px 0 0', color: '#f0f2f6', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, fontFamily: 'inherit' }}
          >+</button>
          <button
            onClick={() => handleZoom(-1)}
            aria-label="Zoom out"
            style={{ width: 32, height: 32, background: '#0a1b36', border: '1px solid #30404e', borderTop: 'none', borderRadius: '0 0 6px 6px', color: '#f0f2f6', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, fontFamily: 'inherit' }}
          >−</button>
        </div>}

        {/* Locate me button */}
        {GMAP_KEY && <button
          onClick={handleLocateMe}
          aria-label="Show my location"
          style={{ position: 'absolute', bottom: '120px', right: '12px', zIndex: 50, width: 36, height: 36, borderRadius: '50%', background: '#0f1520', border: '1px solid rgba(192,160,96,0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c0a060" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
          </svg>
        </button>}

        {/* Location toast */}
        {locToast && (
          <div style={{ position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 200, background: '#0f1520', color: '#99a4b8', fontSize: 13, padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            {locToast}
          </div>
        )}

        <HelpTip id="map-pins" position="bottom" style={{ top: '64px', left: '50%', transform: 'translateX(-50%)' }}>
          Tap a pin to preview a DA. Pinch to zoom into your area.
        </HelpTip>

        {/* Compact popup card — max 250px, no scroll */}
        {mapApp && (
          <div style={{ position: 'absolute', bottom: 16, left: 16, zIndex: 500 }}>
            <MapPopupCard
              app={mapApp}
              onViewDetails={() => { setMapApp(null); onViewDADetails?.(mapApp.id); }}
              onClose={() => setMapApp(null)}
            />
          </div>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <WatchZoneManager
          zones={zones}
          addZone={addZone}
          deleteZone={deleteZone}
          updateZoneRadius={updateZoneRadius}
          zApps={zApps}
          theme={theme}
          setSelZone={setSelZone}
          togTrack={togTrack}
          isTr={isTr}
          zonesOnlyMode={zonesOnlyMode}
          setZonesOnlyMode={setZonesOnlyMode}
          selectedZone={selZone}
          onShowOnMap={onShowOnMap}
        />
        <HelpTip id="watch-zones" position="top">Set up watch zones to get alerts for DAs near you.</HelpTip>
      </div>

      <div style={{ borderTop: `1px solid ${theme.border}`, padding: '8px 0', textAlign: 'center', fontSize: 'var(--text-xs)', color: theme.textMuted, flexShrink: 0 }}>
        DA data © State of New South Wales (Department of Planning, Housing and Infrastructure). Licensed under{' '}
        <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" style={{ color: theme.textMuted, textDecoration: 'underline' }}>Creative Commons Attribution</a>
        . Data has been transformed for display.
      </div>
    </div>
    </div>
  );
}
